/**
 * Consultas compartidas por el shell.
 *
 * Las claves incluyen la company activa: cambiar de consorcio tiene que traer
 * otros datos, no reutilizar la cache del anterior. Los menus dependen de los
 * permisos, que dependen de la company; la metadata tambien, porque un campo
 * puede estar restringido por grupo.
 */

import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { claveGuardada, guardarMeta, leerMetaGuardada } from '@/lib/metaGuardada';
import type { MenuNode, ModelSpec } from '@/lib/types';
import type { MetaDeRegistro } from '@/lib/api';
import { useSessionStore, type SessionStatus } from '@/store/session';

/** Clave raiz para invalidar todo lo dependiente de la empresa activa. */
export const QUERY_KEYS = {
  menus: (companyId: number | null) => ['menus', companyId] as const,
  /**
   * `recordId` forma parte de la clave: la variante con `?id=` trae el `enabled`
   * de las acciones evaluado contra ESE registro, y guardarla bajo la misma
   * clave que la del modelo haria que la lista dibujara la botonera del ultimo
   * formulario abierto.
   */
  meta: (companyId: number | null, modelName: string, recordId: number | null = null) =>
    ['meta', companyId, modelName, recordId] as const,
};

/**
 * Arbol de navegacion. Cambia solo al instalar modulos o al mover permisos, asi
 * que se cachea cinco minutos y no se refetchea al volver a la pestana.
 */
/**
 * ¿Ya se puede pedir lo que depende de estar adentro?
 *
 * Con la sesión resuelta, obvio. Y también MIENTRAS se está resolviendo si
 * hay un token guardado: en un F5 el menú y la metadata de la pantalla
 * esperaban a que `/session/` volviera —medio segundo fijo— para recién
 * entonces salir. Si el token venció, los tres pedidos reciben 401, comparten
 * un único refresco y se reintentan; si el refresco falla, la sesión cae al
 * ingreso como siempre.
 */
function sePuedePedir(status: SessionStatus): boolean {
  return status === 'authenticated' || (status === 'loading' && api.isAuthenticated());
}

export function useMenusQuery(): UseQueryResult<MenuNode[]> {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const enabled = useSessionStore((estado) => sePuedePedir(estado.status));
  // EL ÁRBOL TAMBIÉN SE GUARDA EN EL NAVEGADOR, con las mismas reglas que la
  // metadata (ver `lib/metaGuardada.ts`). Medido con la demora real: en un F5
  // sobre una lista, la lista esperaba al menú —para saber el filtro del
  // ítem por el que se entró— y el menú esperaba a la sesión: tres pedidos en
  // fila. Con el árbol guardado, la lista sale apenas vuelve la sesión.
  const clave = claveGuardada(companyId, '__menus__');

  return useQuery({
    queryKey: QUERY_KEYS.menus(companyId),
    queryFn: async ({ signal }) => {
      const menus = await api.fetchMenus(signal);
      guardarMeta(clave, menus);
      return menus;
    },
    initialData: () => leerMetaGuardada<MenuNode[]>(clave)?.spec,
    initialDataUpdatedAt: () => leerMetaGuardada<MenuNode[]>(clave)?.t,
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Las opciones de la consulta de metadata DEL MODELO (sin id), para quien la
 * necesite fuera de un componente —`ensureQueryData`, un prefetch—: la misma
 * clave, la misma función y lo mismo guardado que `useMetaQuery`, así la
 * caché es UNA sola.
 */
export function opcionesDeMetaDelModelo(
  companyId: number | null,
  modelName: string,
): UseQueryOptions<ModelSpec, Error, ModelSpec, ReturnType<typeof QUERY_KEYS.meta>> {
  const clave = claveGuardada(companyId, modelName);
  return {
    queryKey: QUERY_KEYS.meta(companyId, modelName, null),
    queryFn: async ({ signal }) => {
      const spec = await api.fetchMeta(modelName, null, signal);
      guardarMeta(clave, spec);
      return spec;
    },
    initialData: () => leerMetaGuardada(clave)?.spec,
    initialDataUpdatedAt: () => leerMetaGuardada(clave)?.t,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
}

/**
 * Metadata de un modelo. Es la llamada que sostiene todo el renderer.
 *
 * Sin `recordId` solo cambia con un deploy y se cachea largo. Con `recordId` la
 * respuesta incluye el `enabled` de las acciones evaluado contra ese registro
 * —reglas de fila y dominio de disponibilidad— y por eso caduca rapido: el
 * estado del registro cambia justamente cuando el usuario aprieta uno de esos
 * botones, y una botonera cacheada diez minutos mentiria sobre lo que se puede
 * hacer ahora.
 */
export function useMetaQuery(
  modelName: string | null,
  recordId: number | null = null,
): UseQueryResult<ModelSpec> {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const autenticado = useSessionStore((estado) => sePuedePedir(estado.status));

  // La variante sin id sale de lo guardado en el navegador mientras llega la
  // nueva (ver `opcionesDeMetaDelModelo`); la de un registro no se guarda.
  const delModelo = recordId === null ? opcionesDeMetaDelModelo(companyId, modelName ?? '') : null;
  return useQuery({
    queryKey: QUERY_KEYS.meta(companyId, modelName ?? '', recordId),
    queryFn: delModelo?.queryFn ?? (({ signal }) => api.fetchMeta(modelName as string, recordId, signal)),
    ...(delModelo === null
      ? {}
      : { initialData: delModelo.initialData, initialDataUpdatedAt: delModelo.initialDataUpdatedAt }),
    enabled: autenticado && modelName !== null && modelName !== '',
    // Dos minutos y no diez segundos para la variante con id: todo lo que
    // cambia el estado de un registro —guardar, una acción, una grilla
    // embebida— ya invalida estas claves explícitamente (`useInvalidateModel`,
    // `useInvalidateData`), así que volver a pedirla al reabrir la ficha a los
    // once segundos era pagar medio segundo por una respuesta idéntica. Lo que
    // queda afuera es lo que cambió OTRA persona en esos dos minutos.
    staleTime: recordId === null ? 10 * 60 * 1000 : 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (intentos, error) => {
      // Un 403/404 de metadata no mejora reintentando: el modelo no existe o el
      // usuario no lo puede leer.
      const status = (error as { status?: number }).status;
      if (status === 403 || status === 404) return false;
      return intentos < 2;
    },
  });
}


/**
 * Lo que devuelve `useMetaDeFicha`: la metadata ya armada, o por qué no. Es una
 * unión discriminada como la de React Query, para que después de descartar
 * `isPending` e `isError` el tipo sepa que `data` está.
 */
export type MetaDeFicha =
  | { data: undefined; isPending: true; isError: false; error: null; refetch: () => void }
  | { data: undefined; isPending: false; isError: true; error: Error; refetch: () => void }
  | { data: ModelSpec; isPending: false; isError: false; error: null; refetch: () => void };

/**
 * La metadata para ABRIR UNA FICHA: la del modelo más la botonera evaluada
 * contra ese registro, pedidas EN PARALELO.
 *
 * Antes se pedía `useMetaQuery(modelo, id)`: la metadata entera con `?id=`,
 * 60-150 KB que no se podían cachear largo porque llevaban el id. Lo único
 * que depende del registro es el `enabled` y el motivo de cada botón: eso
 * viene ahora de `/meta/<modelo>/acciones/?id=`, que pesa unos pocos KB, y se
 * superpone a la metadata del modelo, que sí se cachea diez minutos y casi
 * siempre ya está —se viene de la lista—. Así abrir una ficha deja de bajar la
 * metadata cada vez.
 *
 * La clave del mapa cuelga de la de la metadata con id
 * (`[...QUERY_KEYS.meta(c, m, id), 'acciones']`): todo lo que invalidaba
 * aquélla —guardar, una acción— invalida ésta sin tocar nada.
 *
 * Sin `recordId` es exactamente `useMetaQuery(modelo)`.
 */
export function useMetaDeFicha(modelName: string | null, recordId: number | null): MetaDeFicha {
  const companyId = useSessionStore((estado) => estado.activeCompanyId);
  const autenticado = useSessionStore((estado) => estado.status === 'authenticated');
  const base = useMetaQuery(modelName);
  const acciones = useQuery({
    queryKey: [...QUERY_KEYS.meta(companyId, modelName ?? '', recordId), 'acciones'] as const,
    queryFn: ({ signal }) => api.fetchMetaAcciones(modelName as string, recordId as number, signal),
    enabled: autenticado && modelName !== null && modelName !== '' && recordId !== null,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (intentos, error) => {
      const status = (error as { status?: number }).status;
      if (status === 403 || status === 404) return false;
      return intentos < 2;
    },
  });

  const refetch = (): void => {
    void base.refetch();
    if (recordId !== null) void acciones.refetch();
  };
  // Sólo la metadata DEL MODELO puede tirar abajo la pantalla: sin ella no hay
  // formulario que dibujar.
  if (base.isError) return { data: undefined, isPending: false, isError: true, error: base.error, refetch };
  if (base.isPending) {
    return { data: undefined, isPending: true, isError: false, error: null, refetch };
  }
  if (recordId === null) return { data: base.data, isPending: false, isError: false, error: null, refetch };

  // Con una botonera ya evaluada se usa ésa, aunque la relectura que vino
  // después haya fallado: es el último estado conocido y es mejor que nada.
  if (acciones.data !== undefined) {
    return { data: superponer(base.data, acciones.data), isPending: false, isError: false, error: null, refetch };
  }

  // LA FICHA NO ESPERA A LA BOTONERA. Con la metadata del modelo alcanza para
  // dibujar el formulario y arrancar todo lo demás —datos, adjuntos,
  // seguimiento, grillas— en la misma tanda que la botonera. Antes la pantalla
  // esperaba la botonera para montarse, y recién ahí salían esos pedidos: dos
  // tandas en fila, medio segundo fijo cada una.
  //
  // Mientras la botonera llega NO se ofrece ningún botón: ofrecer encendido uno
  // que quizás no se puede apretar sería mentir medio segundo, y ofrecerlos
  // apagados los mandaba a la línea de «acciones que todavía no se pueden»,
  // que contaba mal y hacía saltar la cabecera bajo el cursor.
  //
  // Y SI LA BOTONERA FALLA, LA FICHA SIGUE EN PANTALLA. El formulario ya está
  // montado y quizás con cambios sin guardar: cambiarlo por una pantalla de
  // error los perdería sin aviso. Los botones quedan apagados con el motivo.
  if (acciones.isError) {
    return { data: conLaBotoneraApagada(base.data, SIN_VERIFICAR), isPending: false, isError: false, error: null, refetch };
  }
  return { data: sinBotonera(base.data), isPending: false, isError: false, error: null, refetch };
}

const SIN_VERIFICAR =
  'No se pudo verificar si se puede usar en este registro. Volvé a abrir la ficha para reintentar.';

/** La metadata del modelo sin ningún botón: la botonera todavía no llegó. */
function sinBotonera(base: ModelSpec): ModelSpec {
  return { ...base, actions: [], reports: [], mailing: null };
}

/** La metadata del modelo con cada botón apagado y el mismo motivo. */
function conLaBotoneraApagada(base: ModelSpec, motivo: string): ModelSpec {
  return {
    ...base,
    actions: (base.actions ?? []).map((accion) => ({ ...accion, enabled: false, disabled_reason: motivo })),
    reports: (base.reports ?? []).map((reporte) => ({ ...reporte, enabled: false, disabled_reason: motivo })),
    mailing:
      base.mailing === undefined || base.mailing === null
        ? base.mailing
        : { ...base.mailing, enabled: false, disabled_reason: motivo },
  };
}

/**
 * La metadata del modelo con el estado de cada botón evaluado contra un
 * registro. Se superpone POR NOMBRE: el mapa liviano trae, de cada acción y de
 * cada reporte, sólo `enabled` y `disabled_reason`; lo demás —etiqueta,
 * parámetros, ayuda— es el de la metadata del modelo, que es idéntico.
 */
function superponer(base: ModelSpec, delta: MetaDeRegistro): ModelSpec {
  const acciones = new Map(delta.actions.map((a) => [a.name, a]));
  const reportes = new Map(delta.reports.map((r) => [r.name, r]));
  return {
    ...base,
    actions: (base.actions ?? []).map((accion) => {
      const estado = acciones.get(accion.name);
      return estado === undefined
        ? accion
        : { ...accion, enabled: estado.enabled, disabled_reason: estado.disabled_reason };
    }),
    reports: (base.reports ?? []).map((reporte) => {
      const estado = reportes.get(reporte.name);
      return estado === undefined
        ? reporte
        : { ...reporte, enabled: estado.enabled, disabled_reason: estado.disabled_reason };
    }),
    mailing: delta.mailing,
  };
}
